import { DeviceIntegrity, type DeviceIntegrityResult } from './plugin';

import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import logger from '@/services/logger';
import { showWarnToast } from '@/utils/toast';
import { t } from '@/utils/i18n';

export type { DeviceIntegrityResult } from './plugin';

const CLEAN: DeviceIntegrityResult = { compromised: false, signals: [] };

/**
 * Emulators and simulators trip several of the heuristics legitimately - they
 * run with a writable system partition and ship developer tooling a physical
 * device does not. Reporting them would drown the real signal.
 */
const isPhysicalDevice = async (): Promise<boolean> => {
  const { isVirtual } = await Device.getInfo();
  return !isVirtual;
};

/**
 * Runs the root/jailbreak heuristics and reports the outcome. Purely
 * observational: the result is logged and, when a device looks modified, the
 * driver is shown a warning that dismisses itself. Nothing is blocked.
 *
 * Never throws - a failure to determine integrity must not stop the app
 * starting.
 */
export const reportDeviceIntegrity =
  async (): Promise<DeviceIntegrityResult> => {
    if (!Capacitor.isNativePlatform()) return CLEAN;

    try {
      if (!(await isPhysicalDevice())) return CLEAN;

      const result = await DeviceIntegrity.check();

      // Flattened rather than logged as an object so the signals survive
      // logcat and CloudWatch as readable text.
      logger.info(
        `deviceIntegrity - platform: ${Capacitor.getPlatform()}, ` +
          `compromised: ${result.compromised}, ` +
          `signals: [${result.signals.join(', ')}]`,
      );

      if (result.compromised) {
        await showWarnToast(t('toast.deviceIntegrityWarning'));
      }

      return result;
    } catch (error: any) {
      logger.error(`deviceIntegrity - failed: ${error?.message ?? error}`);
      return CLEAN;
    }
  };
